import { createContext, useCallback, useContext, useMemo, useState } from "react";
import {
	AppTheme,
	applyThemeToDocument,
	getStoredTheme,
	persistTheme,
} from "../services/theme/theme.service";

interface ThemeContextValue {
	theme: AppTheme;
	setTheme: (theme: AppTheme) => void;
	toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Holds the active light/dark theme, persists changes and keeps the <html>
 * data-theme attribute in sync. The initial value is read from localStorage so
 * it matches whatever index.tsx already applied before the first paint.
 */
export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
	const [theme, setThemeState] = useState<AppTheme>(() => getStoredTheme());

	const setTheme = useCallback((next: AppTheme) => {
		setThemeState(next);
		persistTheme(next);
		applyThemeToDocument(next);
	}, []);

	const toggleTheme = useCallback(() => {
		setThemeState((prev) => {
			const next: AppTheme = prev === "dark" ? "light" : "dark";
			persistTheme(next);
			applyThemeToDocument(next);
			return next;
		});
	}, []);

	const value = useMemo<ThemeContextValue>(
		() => ({ theme, setTheme, toggleTheme }),
		[theme, setTheme, toggleTheme]
	);

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
	const ctx = useContext(ThemeContext);
	if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
	return ctx;
};
